<template>
    <v-container fluid>
        <!-- ── Toolbar ─────────────────────────────────────────────────────── -->
        <v-row>
            <v-col class="text-left">
                <v-btn v-if="!inEditMode" class="ml-8 mr-2" color="primary" @click="createInvoice">
                    <v-icon small>fas fa-file-invoice-dollar</v-icon>&nbsp;{{ $t('buttons.createInvoice') }}
                </v-btn>
            </v-col>
            <v-col class="text-right">
                <template v-if="inEditMode">
                    <v-btn class="ml-2 mr-2" color="primary" @click="saveClick(false)">
                        <v-icon small>fa-save</v-icon>&nbsp;{{ $t('buttons.save') }}
                    </v-btn>
                    <v-btn class="ml-2 mr-2" color="primary" @click="saveClick(true)">
                        <v-icon small>fa-save</v-icon>&nbsp;{{ $t('buttons.saveAndExit') }}
                    </v-btn>
                </template>
                <v-btn v-else class="ml-2 mr-2" color="primary" @click="editClick">
                    <v-icon small>fa-edit</v-icon>&nbsp;{{ $t('buttons.edit') }}
                </v-btn>
                <v-btn class="mr-2" @click="backClick">
                    <v-icon small>fa-reply</v-icon>&nbsp;{{ $t('buttons.back') }}
                </v-btn>
            </v-col>
        </v-row>

        <!-- ── Billing list metadata ───────────────────────────────────────── -->
        <v-row dense>
            <v-col class="mb-2 text-h6 text-left ml-8">{{ $t('labels.billingListInformation') }}</v-col>
        </v-row>
        <v-row dense>
            <v-col cols="3" class="ml-4">
                <v-col cols="12">
                    <InputField :readonly="!inEditMode" :label="$t('fields.name')" v-model="billingList.name" />
                </v-col>
                <v-col>
                    <DatePicker
                        :readonly="true"
                        v-model="billingList.billing_period_from"
                        :date-label="$t('fields.billingPeriodFrom')"
                    />
                </v-col>
                <v-col>
                    <DatePicker
                        :readonly="true"
                        v-model="billingList.billing_period_to"
                        :date-label="$t('fields.billingPeriodTo')"
                    />
                </v-col>
                <v-col>
                    <CheckBox :readonly="!inEditMode" v-model="billingList.archive" :label="$t('fields.archiveList')" />
                </v-col>
            </v-col>
            <v-col cols="2">
                <v-col cols="12" class="text-center">
                    <Dropdown
                        v-model="billingList.invoice_to"
                        :readonly="true"
                        :ajax="true"
                        :data="'entities'"
                        :label="$t('fields.invoicetoEntity')"
                    />
                </v-col>
            </v-col>
            <v-col cols="6" class="ml-4">
                <v-col cols="12">
                    <TextArea
                        :readonly="!inEditMode"
                        :maxlength="500"
                        :height="100"
                        :label="$t('fields.information')"
                        v-model="billingList.information"
                    />
                </v-col>
            </v-col>
        </v-row>

        <v-divider />

        <!-- ── Filters + add button ────────────────────────────────────────── -->
        <v-row dense>
            <v-col cols="12">
                <v-col cols="2" class="ml-2 pt-2">
                    <Dropdown
                        v-model="filterItems"
                        :readonly="false"
                        :ajax="false"
                        :data="filters"
                        :label="$t('fields.filters')"
                    />
                </v-col>
                <v-col class="text-right pt-2">
                    <v-btn v-if="inEditMode" class="ml-2 mr-2" color="primary" @click="openAddItemDialog">
                        <v-icon small>fa-plus</v-icon>&nbsp;{{ $t('buttons.addItem') }}
                    </v-btn>
                </v-col>
            </v-col>
        </v-row>

        <!-- ── Positions (vessel / seafarer groups) ────────────────────────── -->
        <span v-for="(posGroup, index) in positions" :key="index">
            <v-row dense>
                <v-col cols="12" class="mb-0 text-h6 ml-8">
                    <v-tooltip bottom>
                        <template v-slot:activator="{on, attrs}">
                            <v-btn icon v-bind="attrs" v-on="on" color="primary" @click="positionUp(posGroup.position)">
                                <v-icon small>fas fa-arrow-up</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t('tooltips.positionUp') }}</span>
                    </v-tooltip>
                    {{ $t('labels.vesselName') }} {{ posGroup.maritime_object }}
                </v-col>
                <v-col cols="12" class="mb-3 text-h7 ml-8">
                    <v-tooltip bottom>
                        <template v-slot:activator="{on, attrs}">
                            <v-btn
                                icon
                                v-bind="attrs"
                                v-on="on"
                                color="primary"
                                @click="positionDown(posGroup.position)"
                            >
                                <v-icon small>fas fa-arrow-down</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t('tooltips.positionDown') }}</span>
                    </v-tooltip>
                    {{ $t('labels.seafarerName') }} {{ posGroup.seafarer }}
                </v-col>
            </v-row>
            <v-row>
                <v-col class="mb-10 ml-8 mr-8">
                    <v-data-table
                        :headers="objectHeaders"
                        :items="itemsForPosition(posGroup.position)"
                        fixed-header
                        dense
                        class="row-item"
                        :items-per-page="-1"
                        :hide-default-footer="true"
                        :no-data-text="$t('labels.noData')"
                    >
                        <template v-slot:item="{item: row}">
                            <tr class="cursor-pointer" @contextmenu.prevent="editItem(row.id, true)">
                                <td @click="editItem(row.id, false)">
                                    <v-simple-checkbox v-model="row.invoice" disabled />
                                </td>
                                <td>
                                    <v-simple-checkbox v-model="row.payslip" disabled />
                                </td>
                                <td @click="editItem(row.id, false)">{{ row.seafarer_display }}</td>
                                <td @click="editItem(row.id, false)">{{ row.rank_display }}</td>
                                <td @click="editItem(row.id, false)" class="font-weight-bold">
                                    {{ row.item_display }}
                                </td>
                                <td @click="editItem(row.id, false)">{{ row.details }}</td>
                                <td @click="editItem(row.id, false)">{{ row.date_display }}</td>
                                <td @click="editItem(row.id, false)" class="text-right">{{ row.quantity_display }}</td>
                                <td @click="editItem(row.id, false)" class="text-right">{{ row.rate_display }}</td>
                                <td @click="editItem(row.id, false)" class="font-weight-bold text-right">
                                    {{ row.sum_display }}
                                </td>
                                <td @click="editItem(row.id, false)" class="text-right">{{ row.vat_display }}</td>
                                <td @click="editItem(row.id, false)" class="text-right">
                                    {{ row.total_with_vat_display }}
                                </td>
                                <td @click="editItem(row.id, false)">{{ row.currency_display }}</td>
                                <td>
                                    <v-tooltip v-if="inEditMode" bottom>
                                        <template v-slot:activator="{on, attrs}">
                                            <v-btn icon v-bind="attrs" v-on="on" @click="removeItem(row.id)">
                                                <v-icon small>fas fa-trash-alt</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>{{ $t('tooltips.delete') }}</span>
                                    </v-tooltip>
                                </td>
                            </tr>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </span>

        <!-- ── Additional items ────────────────────────────────────────────── -->
        <v-row dense>
            <v-col cols="12" class="mb-0 text-h6 ml-8">{{ $t('labels.additional') }}</v-col>
        </v-row>
        <v-row>
            <v-col class="mb-10 ml-8 mr-8">
                <v-data-table
                    :headers="objectHeaders"
                    :items="additionalItems"
                    fixed-header
                    dense
                    class="row-item"
                    :items-per-page="-1"
                    :hide-default-footer="true"
                    :no-data-text="$t('labels.noData')"
                >
                    <template v-slot:item="{item: row}">
                        <tr class="cursor-pointer">
                            <td><v-simple-checkbox v-model="row.invoice" disabled /></td>
                            <td><v-simple-checkbox v-model="row.payslip" disabled /></td>
                            <td @click="editItem(row.id, false)">{{ row.seafarer_display }}</td>
                            <td @click="editItem(row.id, false)">{{ row.rank_display }}</td>
                            <td @click="editItem(row.id, false)" class="font-weight-bold">{{ row.item_display }}</td>
                            <td @click="editItem(row.id, false)">{{ row.details }}</td>
                            <td @click="editItem(row.id, false)">{{ row.date_display }}</td>
                            <td @click="editItem(row.id, false)" class="text-right">{{ row.quantity_display }}</td>
                            <td @click="editItem(row.id, false)" class="text-right">{{ row.rate_display }}</td>
                            <td @click="editItem(row.id, false)" class="font-weight-bold text-right">
                                {{ row.sum_display }}
                            </td>
                            <td @click="editItem(row.id, false)" class="text-right">{{ row.vat_display }}</td>
                            <td @click="editItem(row.id, false)" class="text-right">
                                {{ row.total_with_vat_display }}
                            </td>
                            <td @click="editItem(row.id, false)">{{ row.currency_display }}</td>
                            <td>
                                <v-tooltip v-if="inEditMode" bottom>
                                    <template v-slot:activator="{on, attrs}">
                                        <v-btn icon v-bind="attrs" v-on="on" @click="removeItem(row.id)">
                                            <v-icon small>fas fa-trash-alt</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{ $t('tooltips.delete') }}</span>
                                </v-tooltip>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>

        <!-- ── Totals ──────────────────────────────────────────────────────── -->
        <v-row>
            <v-col cols="2" class="text-left ml-8">
                <CurrencyField
                    :readonly="true"
                    v-model="billingList.payslip_total_net"
                    :label="$t('fields.paysliptTotal')"
                />
            </v-col>
            <v-col cols="4" />
            <v-col cols="2" class="text-right">
                <CurrencyField
                    :readonly="true"
                    v-model="billingList.invoice_total_net"
                    :label="$t('fields.invoiceSum')"
                />
            </v-col>
            <v-col cols="1" class="text-right">
                <CurrencyField :readonly="true" v-model="billingList.net_difference" :label="$t('fields.vat')" />
            </v-col>
            <v-col cols="2" class="text-right">
                <CurrencyField
                    :readonly="true"
                    v-model="billingList.invoice_total_gross"
                    :label="$t('fields.invoiceTotal')"
                />
            </v-col>
        </v-row>

        <!-- ── Item edit / add dialog ──────────────────────────────────────── -->
        <v-row>
            <v-dialog persistent v-model="itemEditDialog" width="600">
                <v-card>
                    <v-card-title>
                        {{ item.id == null ? $t('labels.addItem') : $t('labels.editItem') }}
                    </v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col cols="12">
                                <v-row dense wrap>
                                    <!-- Placement header + invoice/payslip toggles -->
                                    <v-col cols="8" class="mt-3 text-h6">{{ $t('labels.placement') }}</v-col>
                                    <v-col cols="2" class="text-left">
                                        <CheckBox v-model="item.invoice" :label="$t('fields.invoice')" />
                                    </v-col>
                                    <v-col cols="2" class="text-left">
                                        <CheckBox
                                            :readonly="noPlacement"
                                            v-model="item.payslip"
                                            :label="$t('fields.payslip')"
                                        />
                                    </v-col>

                                    <!-- Placement fields (disabled when noPlacement) -->
                                    <v-col cols="12">
                                        <Dropdown
                                            v-model="item.seafarer_id"
                                            :errorBorder="!itemValidation.seafarer_id"
                                            :readonly="noPlacement"
                                            :ajax="true"
                                            :data="'seafarers'"
                                            :label="$t('fields.seafarer')"
                                        />
                                    </v-col>
                                    <v-col cols="12">
                                        <Dropdown
                                            v-model="item.maritime_object_id"
                                            :errorBorder="!itemValidation.maritime_object_id"
                                            :readonly="noPlacement"
                                            :ajax="true"
                                            :data="'maritime-objects'"
                                            :label="$t('fields.maritimeObject')"
                                            @input="onMaritimeObjectChange"
                                        />
                                    </v-col>
                                    <v-col cols="12">
                                        <Dropdown
                                            v-model="item.rank_id"
                                            :errorBorder="!itemValidation.rank_id"
                                            :readonly="noPlacement"
                                            :ajax="true"
                                            :data="'ranks'"
                                            :label="$t('fields.rank')"
                                        />
                                    </v-col>

                                    <!-- Additional / no-placement toggle -->
                                    <v-col cols="8" class="text-h6 mt-3">{{ $t('labels.additional') }}</v-col>
                                    <v-col cols="4" class="text-right">
                                        <CheckBox v-model="noPlacement" :label="$t('fields.noPlacement')" />
                                    </v-col>

                                    <!-- Common item fields -->
                                    <v-col cols="12">
                                        <Dropdown
                                            v-model="item.invoice_item_type_id"
                                            :errorBorder="!itemValidation.invoice_item_type_id"
                                            :ajax="true"
                                            :data="'invoice-item-types'"
                                            :label="$t('fields.item')"
                                        />
                                    </v-col>
                                    <v-col cols="12">
                                        <InputField :label="$t('fields.details')" v-model="item.details" />
                                    </v-col>
                                    <v-col cols="12">
                                        <DatePicker
                                            v-model="item.date_from"
                                            :errorBorder="!itemValidation.date_from"
                                            :date-label="$t('fields.from')"
                                            @input="onDateChange"
                                        />
                                    </v-col>
                                    <v-col cols="12">
                                        <DatePicker
                                            v-model="item.date_to"
                                            :errorBorder="!itemValidation.date_to"
                                            :date-label="$t('fields.to')"
                                            @input="onDateChange"
                                        />
                                    </v-col>
                                    <v-col cols="12">
                                        <Dropdown
                                            v-model="item.currency_id"
                                            :errorBorder="!itemValidation.currency_id"
                                            :disabled="!noPlacement"
                                            :ajax="true"
                                            :data="'currencies-with-exchange'"
                                            :label="$t('fields.currency')"
                                        />
                                    </v-col>

                                    <!-- Quantity / rate / sum -->
                                    <v-col cols="4">
                                        <CurrencyField
                                            :max="100"
                                            :min="0"
                                            v-model="item.quantity"
                                            :errorBorder="!itemValidation.quantity"
                                            :label="$t('fields.qty')"
                                        />
                                    </v-col>
                                    <v-col cols="4">
                                        <CurrencyField
                                            :min="0"
                                            v-model="item.rate"
                                            :errorBorder="!itemValidation.rate"
                                            :label="$t('fields.rate')"
                                        />
                                    </v-col>
                                    <v-col cols="4">
                                        <CurrencyField :readonly="true" v-model="item.sum" :label="$t('fields.sum')" />
                                    </v-col>

                                    <!-- VAT / total -->
                                    <v-col cols="4">
                                        <CurrencyField
                                            :max="100"
                                            :min="0"
                                            v-model="item.vat_percentage"
                                            :errorBorder="!itemValidation.vat_percentage"
                                            :label="$t('fields.vat')"
                                        />
                                    </v-col>
                                    <v-col cols="4" />
                                    <v-col cols="4">
                                        <CurrencyField
                                            :readonly="true"
                                            v-model="item.total_with_vat"
                                            :label="$t('fields.total')"
                                        />
                                    </v-col>

                                    <!-- File uploads -->
                                    <v-col cols="12" class="mr-1">
                                        <MultipleUpload
                                            :accept="'pdf'"
                                            v-model="item.files"
                                            :errorBorder="!itemValidation.files"
                                        />
                                    </v-col>
                                    <v-col cols="12" class="mr-1">
                                        <Upload :readonly="true" v-model="item.file" />
                                    </v-col>
                                </v-row>

                                <!-- Dialog actions -->
                                <v-row>
                                    <v-col
                                        cols="12"
                                        md="6"
                                        class="text-right"
                                        :class="{'text-center': $vuetify.breakpoint.smAndDown}"
                                    >
                                        <v-btn color="primary" class="white--text" @click.stop="saveChanges">
                                            {{ $t('buttons.save') }}
                                        </v-btn>
                                    </v-col>
                                    <v-col
                                        cols="12"
                                        md="6"
                                        class="text-left"
                                        :class="{'text-center': $vuetify.breakpoint.smAndDown}"
                                    >
                                        <v-btn color="secondary" class="white--text" @click.stop="closeDialog">
                                            {{ $t('buttons.cancel') }}
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-dialog>
        </v-row>
    </v-container>
</template>

<script>
import Big from 'big.js';
import InputField from './trinkets/InputField';
import BillingList from '../models/BillingList';
import ItemForBillingList from '../models/ItemForBillingList';
import DatePicker from '@/components/trinkets/DatePicker';
import TextArea from './trinkets/TextArea.vue';
import Dropdown from './trinkets/Dropdown';
import CheckBox from './trinkets/CheckBox';
import CurrencyField from '@/components/trinkets/CurrencyField';
import MultipleUpload from './trinkets/MultipleUpload.vue';
import Upload from '@/components/trinkets/Upload';

const LOCK_KEY = 'billing-list-draft';

export default {
    name: 'BillingListConfirmed',

    components: {InputField, TextArea, Dropdown, CheckBox, DatePicker, CurrencyField, MultipleUpload, Upload},

    data() {
        return {
            billingList: new BillingList({id: this.$route.params.id}),
            item: new ItemForBillingList({}),
            itemValidation: new ItemForBillingList({}, true),

            inEditMode: false,
            lockTimer: null,
            itemEditDialog: false,
            itemDialogReady: false,

            items: [],
            positions: [],

            noPlacement: false,

            filterItems: 3,
            filters: [
                {value: 1, label: 'Invoice'},
                {value: 2, label: 'Payslip'},
                {value: 3, label: 'All'},
            ],

            objectHeaders: [
                {text: this.$t('fields.inv'), sortable: false},
                {text: this.$t('fields.ps'), sortable: false},
                {text: this.$t('fields.seafarer'), sortable: false},
                {text: this.$t('fields.rank'), sortable: false},
                {text: this.$t('fields.item'), sortable: false},
                {text: this.$t('fields.details'), sortable: false},
                {text: this.$t('fields.date'), sortable: false},
                {text: this.$t('fields.qty'), sortable: false},
                {text: this.$t('fields.rate'), sortable: false},
                {text: this.$t('fields.sum'), sortable: false},
                {text: this.$t('fields.vatPercentageSimbol'), sortable: false},
                {text: this.$t('fields.total'), sortable: false},
                {text: this.$t('fields.currency'), sortable: false},
                {text: '', value: 'actions', sortable: false, width: '15%'},
            ],
        };
    },

    computed: {
        /** Items belonging to a specific position group, filtered by invoice/payslip toggle */
        itemsForPosition() {
            return (position) =>
                this.items.filter((i) => i.position === position && !i.additional && this.matchesFilter(i));
        },

        /** Additional items (no placement), filtered by invoice/payslip toggle */
        additionalItems() {
            return this.items.filter((i) => i.additional && this.matchesFilter(i));
        },
    },

    watch: {
        'item.quantity'() {
            this.calculateSums();
        },
        'item.rate'() {
            this.calculateSums();
        },
        'item.vat_percentage'() {
            this.calculateSums();
        },

        'noPlacement'(val) {
            if (val) {
                this.item.seafarer_id = null;
                this.item.maritime_object_id = null;
                this.item.rank_id = null;
                this.item.payslip = false;
                // New No placement item → currency is chosen manually (start empty).
                // When editing an existing item, keep its already-loaded currency.
                if (this.item.id == null) {
                    this.item.currency_id = null;
                }
            } else {
                // Placement re-enabled → currency comes from the maritime object's wage template
                this.loadCurrencyFromWageTemplate(this.item.maritime_object_id);
            }
        },

        'item.files'(files) {
            const invalid = files.filter((f) => {
                const ext = f.original_name.split('.').pop().toLowerCase();
                return ext !== 'pdf';
            });
            if (invalid.length) {
                // Remove invalid entries
                this.item.files = files.filter((f) => {
                    const ext = f.original_name.split('.').pop().toLowerCase();
                    return ext === 'pdf';
                });
                this.$alert(this.$t('alerts.onlyPdf'));
            }
        },
    },

    methods: {
        // ── Helpers ────────────────────────────────────────────────────────────

        matchesFilter(item) {
            if (this.filterItems === 1) return item.invoice === true;
            if (this.filterItems === 2) return item.payslip === true;
            return true; // filterItems === 3 → All
        },

        freshItem() {
            return new ItemForBillingList({});
        },

        freshValidation() {
            return new ItemForBillingList({}, true);
        },

        // ── Data loading ───────────────────────────────────────────────────────

        reloadData() {
            this.$APICall('get', 'billing-list/' + this.billingList.id, {}, (data, success) => {
                if (success) {
                    const d = JSON.parse(JSON.stringify(data));
                    this.billingList = d.item;
                    this.items = d.items;
                    this.positions = d.positions;
                } else {
                    this.$notify(this.$t('alerts.basicError'));
                }
            });
        },

        // ── Navigation / edit mode ─────────────────────────────────────────────

        createInvoice() {
            this.$router.push('/invoices/0/' + this.billingList.id);
        },

        editClick() {
            this.$setLock(this, LOCK_KEY + '|' + this.billingList.id, (data) => {
                if (data.success) {
                    this.inEditMode = true;
                    this.lockTimer = setInterval(
                        () => this.$setLock(this, LOCK_KEY + '|' + this.billingList.id),
                        10000,
                    );
                } else {
                    const name = data.locked_by.first_name + ' ' + data.locked_by.last_name;
                    this.$alert(this.$t('alerts.recordLocked').replace('%name%', name));
                }
            });
        },

        saveClick(goBack) {
            if (!this.billingList.name) {
                this.$alert(this.$t('alerts.insertName'));
                return;
            }

            const list = this.billingList;
            this.$APICall('put', 'billing-lists-confirmed/' + this.billingList.id, {list}, (data, success) => {
                if (success) {
                    this.inEditMode = false;
                    this.clearLock();
                    if (goBack) this.backClick();
                } else {
                    this.$alert(this.$t('alerts.basicError'));
                }
            });
        },

        backClick() {
            this.$router.go(-1);
        },

        clearLock() {
            if (this.lockTimer !== null) {
                clearInterval(this.lockTimer);
                this.lockTimer = null;
            }
            this.$removeLock(this, LOCK_KEY + '|' + this.billingList.id);
        },

        // ── Position reordering ────────────────────────────────────────────────

        positionUp(position) {
            if (position <= 1) return;
            this.$APICall('put', 'billing-lists-position-up/' + this.billingList.id, {position}, (data, success) => {
                if (success) this.reloadData();
                else this.$alert(this.$t('alerts.basicError'));
            });
        },

        positionDown(position) {
            this.$APICall('put', 'billing-lists-position-down/' + this.billingList.id, {position}, (data, success) => {
                if (success) this.reloadData();
                else this.$alert(this.$t('alerts.basicError'));
            });
        },

        // ── Item dialog ────────────────────────────────────────────────────────

        openAddItemDialog() {
            this.item = this.freshItem();
            this.itemValidation = this.freshValidation();
            this.noPlacement = false;
            this.itemDialogReady = false;
            this.itemEditDialog = true;
            this.$nextTick(() => {
                this.itemDialogReady = true;
            });
        },

        editItem(itemId) {
            this.item = this.freshItem();
            this.itemValidation = this.freshValidation();
            this.itemDialogReady = false;

            this.$APICall('get', 'item-for-billing/' + itemId, {}, (data, success) => {
                if (success) {
                    this.item = data.item;
                    this.noPlacement = this.item.placement_id == null;
                    this.itemEditDialog = true;
                    this.$nextTick(() => {
                        this.itemDialogReady = true;
                    });
                } else {
                    this.$notify(this.$t('alerts.basicError'));
                }
            });
        },

        closeDialog() {
            this.itemEditDialog = false;
            this.itemDialogReady = false;
            this.item = this.freshItem();
            this.itemValidation = this.freshValidation();
        },

        /**
         * When a placement is active (noPlacement === false), the currency is not
         * chosen manually but taken from the selected maritime object's wage template.
         */
        onMaritimeObjectChange(maritimeObjectId) {
            if (this.noPlacement) return;
            this.loadCurrencyFromWageTemplate(maritimeObjectId);
        },

        loadCurrencyFromWageTemplate(maritimeObjectId) {
            if (this.noPlacement || !maritimeObjectId) return;
            this.$APICall('get', 'wage-templates/' + maritimeObjectId, {}, (data, success) => {
                if (success && data) {
                    this.item.currency_id = data.currency_id;
                }
            });
        },

        // ── Item CRUD ──────────────────────────────────────────────────────────

        saveChanges() {
            if (!this.validateItem()) return;

            const isNew = this.item.id == null;
            const action = isNew ? 'post' : 'put';
            const url = isNew ? 'item-for-billing' : 'item-for-billing/' + this.item.id;

            if (isNew) {
                this.item.billing_list_id = this.billingList.id;
                this.item.seniority = 1;
            }

            this.item.additional = this.noPlacement;
            this.item.invoice_to_id = this.billingList.invoice_to;

            const item = this.item;
            this.$APICall(action, url, {item}, (data, success) => {
                if (success) {
                    if (data.message === 1) {
                        this.$notify(this.$t('alerts.noPlacementToAttach'));
                    } else {
                        this.closeDialog();
                        this.reloadData();
                    }
                } else {
                    this.$notify(this.$t('alerts.basicError'));
                }
            });
        },

        removeItem(itemId) {
            this.$verify(this.$t('alerts.removeItemQuestion'), (confirmed) => {
                if (!confirmed) return;
                this.$APICall('post', 'remove-item-from-billing/' + itemId, {}, (data, success) => {
                    if (success) this.reloadData();
                    else this.$alert(this.$t('alerts.basicError'));
                });
            });
        },

        // ── Calculations ───────────────────────────────────────────────────────

        calculateSums() {
            const qty = this.item.quantity;
            const rate = this.item.rate;
            const vat = this.item.vat_percentage;

            if (qty == null || qty === '' || rate == null || rate === '') {
                this.item.sum = null;
                this.item.total_with_vat = null;
                return;
            }

            const subtotal = Big(rate).times(Big(qty));
            this.item.sum = subtotal.toFixed(2);

            if (vat != null && vat !== '') {
                const tax = subtotal.times(Big(vat)).div(100).round(2);
                this.item.total_with_vat = subtotal.plus(tax).toFixed(2);
            } else {
                this.item.total_with_vat = subtotal.toFixed(2);
            }
        },

        onDateChange() {
            if (!this.itemDialogReady || this.noPlacement) return;
            this.calculateQuantity();
        },

        calculateQuantity() {
            const from = new Date(this.item.date_from);
            const to = new Date(this.item.date_to);

            if (isNaN(from.getTime()) || isNaN(to.getTime())) return;

            const diffMs = to.getTime() - from.getTime();
            const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;

            if (diffDays > 0) {
                this.item.quantity = diffDays;
            }
        },

        // ── Validation ─────────────────────────────────────────────────────────

        validateItem() {
            let valid = true;
            const v = this.itemValidation;
            const i = this.item;

            if (!this.noPlacement) {
                v.seafarer_id = !!i.seafarer_id;
                v.maritime_object_id = !!i.maritime_object_id;
                v.rank_id = !!i.rank_id;
                if (!v.seafarer_id || !v.maritime_object_id || !v.rank_id) valid = false;
            } else {
                v.seafarer_id = true;
                v.maritime_object_id = true;
                v.rank_id = true;
            }

            v.invoice_item_type_id = !!i.invoice_item_type_id;
            if (!v.invoice_item_type_id) valid = false;

            if (!i.quantity || i.quantity == 0) {
                v.quantity = false;
                valid = false;
            } else {
                v.quantity = true;
                v.date_from = !!i.date_from;
                v.date_to = !!i.date_to;
                if (!v.date_from || !v.date_to) valid = false;
            }

            v.rate = !(!i.rate || i.rate == 0);
            if (!v.rate) valid = false;

            v.currency_id = !(!i.currency_id || i.currency_id == 0);
            if (!v.currency_id) valid = false;

            v.vat_percentage = i.vat_percentage != null;
            if (!v.vat_percentage) valid = false;

            return valid;
        },
    },

    // ── Lifecycle ──────────────────────────────────────────────────────────────

    mounted() {
        if (!this.$myStore.state.loggedIn) {
            this.$router.replace('login');
            return;
        }

        if (this.billingList.id != 0) {
            this.reloadData();
        } else {
            this.inEditMode = true;
        }
    },

    beforeRouteLeave(to, from, next) {
        if (!this.inEditMode) {
            this.clearLock();
            return next(true);
        }

        this.$verify(this.$t('alerts.leaveQuestion'), (confirmed) => {
            if (confirmed) {
                this.clearLock();
                next(true);
            } else {
                next(false);
            }
        });
    },
};
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
</style>
